/**
 * Pricing Scenarios Component
 * Displays pricing scenario simulations
 */

import { useState, useMemo } from 'react';
import type { PricingScenario } from '@/lib/api/reports';
import clsx from 'clsx';
import { AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';

interface PricingScenariosProps {
  scenarios: PricingScenario[];
  className?: string;
}

export function PricingScenarios({ scenarios, className }: PricingScenariosProps) {
  const [selectedId, setSelectedId] = useState(scenarios[0]?.id || 'current');

  const selectedScenario = useMemo(
    () => scenarios.find((s) => s.id === selectedId) || scenarios[0],
    [scenarios, selectedId]
  );

  const formatCurrency = (value: number) => `¥${value.toFixed(2)}`;
  const formatPercent = (value: number) => `${value.toFixed(1)}%`;

  const getProfitColor = (margin: number) => {
    if (margin >= 20) return 'text-green-600';
    if (margin >= 0) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <div className={clsx('space-y-6', className)}>
      {/* Alert for low/negative margin */}
      {selectedScenario && selectedScenario.profit_margin < 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-700 font-medium">
              警告: 当前方案预计亏损 {formatCurrency(Math.abs(selectedScenario.projected_profit))}/100用户
            </p>
          </div>
        </div>
      )}

      {selectedScenario && selectedScenario.profit_margin >= 0 && selectedScenario.profit_margin < 20 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-700 font-medium">
              注意: 毛利率仅 {formatPercent(selectedScenario.profit_margin)}，建议优化成本结构
            </p>
          </div>
        </div>
      )}

      {/* Scenario Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {scenarios.map((scenario) => (
          <div
            key={scenario.id}
            onClick={() => setSelectedId(scenario.id)}
            className={clsx(
              'bg-white rounded-xl p-5 border-2 cursor-pointer transition-all hover:shadow-md',
              selectedId === scenario.id
                ? 'border-blue-500 bg-blue-50/50'
                : 'border-gray-200 hover:border-blue-300'
            )}
          >
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              {scenario.name}
            </h4>
            <p className="text-xs text-gray-500 mb-4 line-clamp-2">
              {scenario.description}
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <div
                  className={clsx(
                    'text-lg font-bold',
                    getProfitColor(scenario.profit_margin)
                  )}
                >
                  {formatPercent(scenario.profit_margin)}
                </div>
                <div className="text-xs text-gray-500">毛利率</div>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <div className="text-lg font-bold text-gray-900">
                  {formatCurrency(scenario.projected_profit)}
                </div>
                <div className="text-xs text-gray-500">净利润/100人</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Projection Summary */}
      {selectedScenario && (
        <div className="bg-gray-50 rounded-xl p-6">
          <h4 className="text-sm font-medium text-gray-600 mb-4">
            方案预测详情 (100用户/月)
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <ProjectionItem
              label="预估收入"
              value={formatCurrency(selectedScenario.projected_revenue)}
              variant="profit"
            />
            <ProjectionItem
              label="预估成本"
              value={formatCurrency(selectedScenario.projected_cost)}
              variant="danger"
            />
            <ProjectionItem
              label="退费损失"
              value={formatCurrency(selectedScenario.projected_refund_loss)}
              variant="warning"
              subtitle={`退费率 ${formatPercent(selectedScenario.refund_rate * 100)}`}
            />
            <ProjectionItem
              label="净利润"
              value={formatCurrency(selectedScenario.projected_profit)}
              variant={selectedScenario.projected_profit >= 0 ? 'profit' : 'danger'}
              icon={
                selectedScenario.projected_profit >= 0 ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )
              }
            />
            <ProjectionItem
              label="毛利率"
              value={formatPercent(selectedScenario.profit_margin)}
              variant={
                selectedScenario.profit_margin >= 20
                  ? 'profit'
                  : selectedScenario.profit_margin >= 0
                    ? 'warning'
                    : 'danger'
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface ProjectionItemProps {
  label: string;
  value: string;
  subtitle?: string;
  variant?: 'default' | 'profit' | 'warning' | 'danger';
  icon?: React.ReactNode;
}

function ProjectionItem({
  label,
  value,
  subtitle,
  variant = 'default',
  icon,
}: ProjectionItemProps) {
  const variantStyles = {
    default: 'text-gray-900',
    profit: 'text-green-600',
    warning: 'text-amber-600',
    danger: 'text-red-600',
  };

  return (
    <div className="text-center">
      <div className={clsx('text-xl font-bold flex items-center justify-center gap-1', variantStyles[variant])}>
        {icon}
        {value}
      </div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
      {subtitle && <div className="text-xs text-gray-400">{subtitle}</div>}
    </div>
  );
}
